"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Alert, AlertTitle } from "../ui/alert";
import Link from "next/link";
import { Eye, EyeOff, Ban } from "lucide-react";
import { LoginFormValues, loginSchema } from "../../../features/auth/schemas";
import { login } from "../../../features/auth/api";
import { useRouter, useSearchParams } from "next/navigation";
import { ensureDefaultEntity } from "../../../features/context/session";

// N'accepte qu'un chemin relatif interne (ex: /entity/xyz/operations/abc?tab=CGMS) —
// jamais une URL absolue ni un chemin protocol-relative (//host/...), qui
// détourneraient la redirection post-connexion vers un site tiers.
function safeCallbackUrl(url: string | null): string | null {
    if (!url || !url.startsWith("/") || url.startsWith("//")) return null;
    return url;
}

/**
 * Login form component for user authentication.
 * @returns Login form JSX element
 */
export default function LoginForm() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const [authError, setAuthError] = useState("");
    const [showPwd, setShowPwd] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<LoginFormValues>({
        resolver: zodResolver(loginSchema),
    });

    const onSubmit = async (data: LoginFormValues) => {
        setAuthError("")
        const result = await login(data);
    
        if(result?.error) {
            setAuthError("Email ou mot de passe incorrect..");
            return;
        }

        // Assigner une entité par défaut si aucune n'est sélectionnée
        try {
            await ensureDefaultEntity();
        } catch (error) {
            console.error("Erreur lors de l'assignation de l'entité par défaut", error);
        }

        router.refresh();
        router.push(safeCallbackUrl(searchParams.get("callbackUrl")) || "/dashboard");
    }


    return (
    <form onSubmit={handleSubmit(onSubmit)} method="POST">
        <div className="space-y-2">
            <div className="grid gap-4">
                <div className="grid gap-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="gap-1">
                        <Input
                            id="email"
                            type="email"
                            autoFocus
                            placeholder="you@example.com"
                            {...register("email")}
                        />
                        {errors.email && (
                            <p className="text-sm font-medium text-red-500">
                                {errors.email.message}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="grid gap-2">
                <div className="flex justify-between">
                    <Label htmlFor="password">Mot de passe</Label>
                    <Link href="/forgot-password" className="text-sm hover:underline">
                        Mot de passe oublié ?
                    </Link>
                </div>
                <div className="relative">
                    <Input
                        id="password"
                        type={showPwd ? "text" : "password"}
                        {...register("password")}
                        disabled={isSubmitting}
                    />
                    <button
                        type="button"
                        aria-label={
                            showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"
                        }
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowPwd((s) => !s)}
                        tabIndex={-1}
                        >
                        {showPwd ? (
                            <EyeOff className="w-4 h-4" />
                        ) : (
                            <Eye className="w-4 h-4" />
                        )}
                    </button>
                    {errors.password && (
                        <p className="text-sm font-medium text-red-500">
                            {errors.password.message}
                        </p>
                    )}
                </div>
            </div>
            {/* Auth error */}
            {authError && (
                <Alert className="text-sm text-center text-red-600">
                    <Ban/>
                    <AlertTitle>{authError}</AlertTitle>
                </Alert>
            )}
            <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? "Connexion..." : "Se connecter"}
            </Button>
        </div>
    </form>
    )
}